import React, { useEffect, useRef } from "react";
import WatchDemo from "../assets/WatchDemo.mp4";
import { IoIosClose, IoMdPlay, IoMdPause } from "react-icons/io";
import { MdFullscreen, MdVolumeOff, MdVolumeUp } from "react-icons/md";

const VideoModal = ({ isOpen, onClose }) => {
  const videoRef = useRef(null);
  const modalRef = useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [isMuted, setIsMuted] = React.useState(false); 
  const [showControls, setShowControls] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      videoRef.current.muted = false;
      setIsMuted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
      if (isMuted) {
        videoRef.current.muted = false;
        setIsMuted(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleTouchStart = () => {
    if (isTouchDevice) {
      setShowControls(true);
      setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const getVideoContentRect = (video) => {
    const rect = video.getBoundingClientRect();
    if (!video.videoWidth || !video.videoHeight) return rect;

    const videoRatio = video.videoWidth / video.videoHeight;
    const elementRatio = rect.width / rect.height;

    if (elementRatio > videoRatio) {
      const height = rect.height;
      const width = height * videoRatio;
      return {
        left: rect.left + (rect.width - width) / 2,
        top: rect.top,
        width,
        height,
      };
    }

    const width = rect.width;
    const height = width / videoRatio;
    return {
      left: rect.left,
      top: rect.top + (rect.height - height) / 2,
      width,
      height,
    };
  };

  const handleVideoClick = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const rect = getVideoContentRect(video);
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const zoneWidth = rect.width * 0.4;
    const zoneHeight = rect.height * 0.4;

    const inCenter =
      x >= 0 &&
      y >= 0 &&
      x <= rect.width &&
      y <= rect.height &&
      Math.abs(x - centerX) < zoneWidth / 2 &&
      Math.abs(y - centerY) < zoneHeight / 2;

    if (!inCenter) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play();
      setIsPlaying(true);
      if (isMuted) {
        video.muted = false;
        setIsMuted(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50 sm:p-4 animate-in fade-in duration-300"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-modal-title"
    >
      <div className="absolute top-0 right-0 sm:top-4 sm:right-4 z-50">
        <button
          onClick={onClose}
          className="text-black hover:text-gray-800 transition-colors p-2"
          aria-label="Close video modal"
        >
          <IoIosClose size={40} className="text-white" />
        </button>
      </div>
      <div className="relative w-full max-w-5xl mx-auto bg-black sm:rounded-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
        <div
          className="relative aspect-video bg-black group"
          onMouseEnter={() => !isTouchDevice && setShowControls(true)}
          onMouseLeave={() => !isTouchDevice && setShowControls(false)}
          onTouchStart={() => !isTouchDevice && handleTouchStart}
        >
          <video
            ref={videoRef}
            src={WatchDemo}
            className="w-full h-full object-contain"
            autoPlay
            muted={isMuted}
            loop
            playsInline
            controls={isTouchDevice}
            onClick={handleVideoClick}
          />
          {!isTouchDevice && (
          <div
            className={`absolute inset-0 pointer-events-none bg-gradient-to-t from-black/50 via-transparent to-transparent transition-opacity duration-300 ${
              showControls ? "opacity-100" : "opacity-0"
            }`}
          >
            <div
              className={`absolute top-0 left-0 right-0 pointer-events-auto bg-gradient-to-b from-black/70 to-transparent p-4 z-10 transition-opacity duration-300 ${
                showControls ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="flex items-center justify-between">
                <h2
                  id="video-modal-title"
                  className="text-black text-lg font-semibold"
                >
                  Watch Demo
                </h2>
                <button
                  onClick={onClose}
                  className="text-black hover:text-gray-800 transition-colors p-2 rounded-full hover:bg-white/10"
                  aria-label="Close video modal"
                >
                  <IoIosClose size={30} />
                </button>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              {/* <button
                onClick={togglePlayPause}
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-black rounded-full p-4 transition-all duration-200 hover:scale-110"
                aria-label={isPlaying ? "Pause video" : "Play video"}
              >
                {isPlaying ? <IoMdPause size={32} /> : <IoMdPlay size={32} />}
              </button> */}
            </div>
            <div className="absolute bottom-0 left-0 right-0 pointer-events-auto p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={togglePlayPause}
                    className="text-black hover:text-gray-300 transition-colors"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <IoMdPause size={20} />
                    ) : (
                      <IoMdPlay size={20} />
                    )}
                  </button>

                  <button
                    onClick={toggleMute}
                    className="text-black hover:text-gray-300 transition-colors"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <MdVolumeOff size={20} />
                    ) : (
                      <MdVolumeUp size={20} />
                    )}
                  </button>
                </div>

                <button
                  onClick={toggleFullscreen}
                  className="text-black hover:text-gray-300 transition-colors"
                  aria-label="Fullscreen"
                >
                  <MdFullscreen size={20} />
                </button>
              </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
